<template>
  <div>
    <Modal :title="$t('global.create-site-from-stencil')" @close="close" @confirm="confirm">
      <template #body>
        <LoadingState v-if="status.isLoading || status.isUpdating" />
        <ErrorState v-else-if="status.isErrored" />
        <div v-else>
          <div class="h4 pb-3">
            <span class="mr-3 text-secondary">{{ $t("global.plan") }}: </span>
            <span>{{ data.plan.nickname }}</span>
          </div>
          <FormSelect
            name="version"
            class="mb-4"
            v-model="fields.stencilId"
            :label="`${$t('global.stencil')} *`"
            :disabled="loading || hasStencil()"
          >
            <option v-for="item in stencils" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </FormSelect>
        </div>
      </template>
      <template #footer>
        <Btn class="mr-2" variant="light" type="button" :disabled="loading" @click="close">
          {{ $t("global.cancel") }}
        </Btn>
        <Btn variant="primary" @click="confirm" :loading="loading">
          {{ $t("global.site-add") }}
        </Btn>
      </template>
    </Modal>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ItemErrorState";
import FormSelect from "@/components/display/form/FormSelect";
import { fromServer } from "@/utils/translations";
import LoadingState from "@/components/display/LoadingState";
import Modal from "@/components/display/Modal";
import store from "@/store";

export default {
  name: "StencilCreateSiteModal",
  components: {
    Btn,
    ErrorState,
    FormSelect,
    LoadingState,
    Modal,
  },
  props: {
    data: {
      type: Object,
      validator: (value) => {
        const hasSite = !!value.site && typeof value.site === "object";
        const hasPlan = !!value.plan && typeof value.plan === "object";

        return hasSite && hasPlan;
      },
    },
  },
  setup(props, { emit, root }) {
    const loading = ref(false);
    const stencils = computed(() => store.getters["allStencils/getList"]);
    const status = computed(() => store.getters["allStencils/getListStatus"]);
    const fields = reactive({
      stencilId: props.data.stencil?.id,
    });

    const close = () => {
      emit("close");
    };

    const confirm = () => {
      loading.value = true;

      const { plan, stencil } = props.data;

      store
        .dispatch("sites/createFromStencil", {
          planId: plan.id,
          stencilId: fields.stencilId,
        })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "success",
            message: root.$t("toasts.site-adding-message", {
              name:
                stencil?.name ||
                stencils.value?.find((item) => item.id === Number(fields.stencilId))?.name,
            }),
          });
          emit("confirm");
        })
        .catch((e) => {
          const errors = fromServer(e.request.response);
          if (Array.isArray(errors)) {
            errors.forEach((error) => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: error,
                timeout: true,
              });
            });
          } else {
            store.dispatch("toasts/addToast", {
              variant: "error",
              message: errors,
              timeout: true,
            });
          }
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const hasStencil = () => !!props.data?.stencil;

    onMounted(() => {
      if (!hasStencil()) {
        store
          .dispatch("allStencils/getList", { planId: props.data.plan.id })
          .then((stencils) => (fields.stencilId = stencils[0].id));
      }
    });

    return {
      loading,
      close,
      confirm,
      fields,
      hasStencil,
      status,
      stencils,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";
</style>
